import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { X, Plus, Trash2, Loader2, Printer, Save } from "lucide-react";

const emptyItem = () => ({ name: "", qty: 1, rate: 0, amount: 0 });

const BillForm = ({ patient, doctor, initial, initialItems, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => ({
    date: initial?.date || new Date().toISOString().slice(0, 10),
    items: initial?.items?.length
      ? initial.items
      : initialItems?.length
      ? initialItems
      : [emptyItem()],
    discount: initial?.discount ?? 0,
    gst_percent: initial?.gst_percent ?? 0,
    notes: initial?.notes || "",
    status: initial?.status || "Unpaid",
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savedBill, setSavedBill] = useState(isEdit ? initial : null);

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((s, i) => s + (Number(i.qty || 0) * Number(i.rate || 0)), 0);
    const afterDisc = Math.max(subtotal - Number(form.discount || 0), 0);
    const gstAmt = afterDisc * Number(form.gst_percent || 0) / 100;
    return {
      subtotal: round2(subtotal),
      gst_amount: round2(gstAmt),
      total: round2(afterDisc + gstAmt),
    };
  }, [form]);

  const setItem = (idx, key, val) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [key]: val };
      const q = Number(items[idx].qty || 0);
      const r = Number(items[idx].rate || 0);
      items[idx].amount = round2(q * r);
      return { ...f, items };
    });
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        ...form,
        discount: Number(form.discount || 0),
        gst_percent: Number(form.gst_percent || 0),
        items: form.items
          .filter((i) => i.name.trim())
          .map((i) => ({
            name: i.name.trim(),
            qty: Number(i.qty || 0),
            rate: Number(i.rate || 0),
            amount: round2(Number(i.qty || 0) * Number(i.rate || 0)),
          })),
      };
      if (payload.items.length === 0) {
        setErr("Add at least one item");
        setBusy(false);
        return;
      }
      let res;
      if (isEdit) {
        res = await api.put(`/bills/${initial.id}`, payload);
        onSaved?.(res.data, false);
      } else {
        res = await api.post("/bills", { patient_id: patient.id, ...payload });
        onSaved?.(res.data, true);
      }
      setSavedBill(res.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const doPrint = () => {
    const bill = savedBill || {
      bill_no: "DRAFT",
      date: form.date,
      items: form.items,
      discount: Number(form.discount || 0),
      gst_percent: Number(form.gst_percent || 0),
      subtotal: totals.subtotal,
      gst_amount: totals.gst_amount,
      total: totals.total,
      notes: form.notes,
    };
    openPrintWindow(bill, patient, doctor);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-lg">
          <h3 className="font-semibold text-slate-800">
            {isEdit ? "Bill" : "New Bill"} {savedBill?.bill_no && `— ${savedBill.bill_no}`}
          </h3>
          <div className="flex gap-2">
            <button
              onClick={doPrint}
              className="inline-flex items-center gap-1.5 text-sm text-slate-700 border border-slate-300 hover:bg-slate-100 px-3 py-1.5 rounded"
            >
              <Printer size={14} /> Print / PDF
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="px-6 py-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <div className="text-xs text-slate-500">Clinic</div>
              <div className="font-bold text-slate-800 text-lg">{doctor?.clinic_name || doctor?.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {doctor?.phone} {doctor?.email && `• ${doctor.email}`}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-[#0F2A5F]">INVOICE</div>
              {savedBill?.bill_no && <div className="text-sm font-mono text-slate-700 mt-1">{savedBill.bill_no}</div>}
              <div className="text-xs text-slate-500 mt-1">Date: {form.date}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6 bg-slate-50 rounded p-3">
            <div>
              <div className="text-xs text-slate-500">Bill To</div>
              <div className="font-semibold text-slate-800">{patient.name}</div>
              <div className="text-xs text-slate-600">UHID: {patient.uhid} • {patient.phone}</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Bill Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
            </div>
          </div>

          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="border-b-2 border-slate-300 text-slate-600">
                <th className="py-2 text-left">Procedure / Item</th>
                <th className="py-2 text-right w-20">Qty</th>
                <th className="py-2 text-right w-28">Rate (₹)</th>
                <th className="py-2 text-right w-28">Amount (₹)</th>
                <th className="py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <tr key={i} className="border-b border-slate-100 align-top">
                  <td className="py-2 pr-2">
                    <input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="e.g. Root Canal - Molar" className="w-full px-2 py-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]" />
                  </td>
                  <td className="py-2 pr-2">
                    <input type="number" step="0.01" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="w-full px-2 py-1 border border-slate-200 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]" />
                  </td>
                  <td className="py-2 pr-2">
                    <input type="number" step="0.01" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} className="w-full px-2 py-1 border border-slate-200 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]" />
                  </td>
                  <td className="py-2 text-right pr-1 font-medium text-slate-700">
                    {round2(Number(it.qty || 0) * Number(it.rate || 0)).toFixed(2)}
                  </td>
                  <td className="py-2 text-right">
                    <button onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mb-6">
            <button onClick={addItem} className="text-sm text-[#0F2A5F] hover:underline inline-flex items-center gap-1"><Plus size={13} /> Add item</button>
          </div>

          <div className="flex justify-end">
            <div className="w-full md:w-72 space-y-1.5 text-sm">
              <Row label="Subtotal" value={`₹${totals.subtotal.toFixed(2)}`} />
              <div className="flex items-center justify-between">
                <label className="text-slate-600">Discount (₹)</label>
                <input type="number" step="0.01" value={form.discount} onChange={(e) => setForm((f) => ({ ...f, discount: e.target.value }))} className="w-24 px-2 py-1 border border-slate-200 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]" />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-slate-600">GST (%)</label>
                <input type="number" step="0.01" value={form.gst_percent} onChange={(e) => setForm((f) => ({ ...f, gst_percent: e.target.value }))} className="w-24 px-2 py-1 border border-slate-200 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]" />
              </div>
              <Row label="GST Amount" value={`₹${totals.gst_amount.toFixed(2)}`} />
              <div className="flex items-center justify-between pt-2 border-t-2 border-slate-300 mt-1">
                <span className="font-semibold text-slate-800">Total</span>
                <span className="font-bold text-lg text-[#0F2A5F]">₹{totals.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]" placeholder="Notes for the patient" />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <label className="text-xs font-medium text-slate-600">Payment Status</label>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="px-2 py-1.5 border border-slate-300 rounded text-sm">
              <option>Unpaid</option>
              <option>Paid</option>
            </select>
          </div>

          {err && <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 rounded-b-lg bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Close</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 text-sm bg-[#0F2A5F] hover:bg-[#0A1E4A] disabled:opacity-70 text-white rounded-md font-medium inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isEdit || savedBill ? "Update Bill" : "Save Bill"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-600">{label}</span>
    <span className="font-medium text-slate-800">{value}</span>
  </div>
);

function round2(n) { return Math.round(Number(n || 0) * 100) / 100; }

function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

// Build a self-contained printable HTML document in a new window and trigger print.
function openPrintWindow(bill, patient, doctor) {
  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) {
    alert("Please allow pop-ups to print the bill.");
    return;
  }
  const itemRows = (bill.items || [])
    .filter((i) => i.name && i.name.trim())
    .map((i) => {
      const qty = Number(i.qty || 0);
      const rate = Number(i.rate || 0);
      const amt = round2(qty * rate).toFixed(2);
      return `
        <tr>
          <td>${esc(i.name)}</td>
          <td class="right">${qty}</td>
          <td class="right">₹${rate.toFixed(2)}</td>
          <td class="right">₹${amt}</td>
        </tr>`;
    })
    .join("");

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Invoice ${esc(bill.bill_no || "")}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;
      margin: 0; padding: 32px; color: #1e293b;
      background: #fff;
    }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 20px; border-bottom: 2px solid #0F2A5F; }
    .clinic-name { font-size: 20px; font-weight: 800; color: #0F2A5F; margin: 0 0 4px 0; }
    .clinic-info { font-size: 12px; color: #64748b; }
    .invoice-label { font-size: 28px; font-weight: 800; color: #0F2A5F; letter-spacing: 2px; margin: 0; }
    .invoice-no { font-size: 13px; font-family: monospace; color: #334155; margin-top: 4px; }
    .invoice-date { font-size: 12px; color: #64748b; margin-top: 4px; }
    .bill-to { background: #f1f5f9; border-radius: 6px; padding: 12px 16px; margin-bottom: 20px; }
    .bill-to-label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; margin-bottom: 4px; }
    .patient-name { font-weight: 700; color: #0F2A5F; font-size: 15px; }
    .patient-info { font-size: 12px; color: #475569; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    thead th { background: #0F2A5F; color: white; text-align: left; padding: 10px 12px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
    thead th.right { text-align: right; }
    tbody td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
    td.right { text-align: right; }
    .totals { margin-left: auto; width: 320px; font-size: 13px; }
    .totals .row { display: flex; justify-content: space-between; padding: 6px 0; }
    .totals .row.grand { border-top: 2px solid #0F2A5F; padding-top: 10px; margin-top: 6px; font-weight: 700; font-size: 16px; color: #0F2A5F; }
    .notes { margin-top: 30px; padding: 12px; background: #f8fafc; border-left: 3px solid #14B8A6; border-radius: 4px; font-size: 12px; color: #475569; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 11px; color: #94a3b8; }
    .status-badge { display: inline-block; margin-top: 8px; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
    .status-paid { background: #dcfce7; color: #166534; }
    .status-unpaid { background: #fef3c7; color: #92400e; }
    @media print {
      body { padding: 20px; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="clinic-name">${esc(doctor?.clinic_name || doctor?.name || "Clinic")}</div>
      <div class="clinic-info">
        ${doctor?.name && doctor?.clinic_name ? esc(doctor.name) + "<br/>" : ""}
        ${doctor?.clinic_address ? esc(doctor.clinic_address) + "<br/>" : ""}
        ${doctor?.phone ? "Phone: " + esc(doctor.phone) : ""}${doctor?.phone && doctor?.email ? " • " : ""}${doctor?.email ? esc(doctor.email) : ""}
      </div>
    </div>
    <div style="text-align: right;">
      <div class="invoice-label">INVOICE</div>
      ${bill.bill_no ? `<div class="invoice-no">${esc(bill.bill_no)}</div>` : ""}
      <div class="invoice-date">Date: ${esc(bill.date || "")}</div>
      ${bill.status ? `<div class="status-badge status-${(bill.status || "").toLowerCase()}">${esc(bill.status)}</div>` : ""}
    </div>
  </div>

  <div class="bill-to">
    <div class="bill-to-label">Bill To</div>
    <div class="patient-name">${esc(patient.name)}</div>
    <div class="patient-info">UHID: ${esc(patient.uhid || "")} &nbsp;•&nbsp; Phone: ${esc(patient.phone || "")}${patient.email ? "  •  " + esc(patient.email) : ""}</div>
    ${patient.address ? `<div class="patient-info">${esc(patient.address)}</div>` : ""}
  </div>

  <table>
    <thead>
      <tr>
        <th>Procedure / Item</th>
        <th class="right" style="width: 70px;">Qty</th>
        <th class="right" style="width: 100px;">Rate</th>
        <th class="right" style="width: 110px;">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
    </tbody>
  </table>

  <div class="totals">
    <div class="row"><span>Subtotal</span><span>₹${Number(bill.subtotal || 0).toFixed(2)}</span></div>
    ${Number(bill.discount || 0) > 0 ? `<div class="row"><span>Discount</span><span>- ₹${Number(bill.discount).toFixed(2)}</span></div>` : ""}
    ${Number(bill.gst_percent || 0) > 0 ? `<div class="row"><span>GST (${Number(bill.gst_percent).toFixed(2)}%)</span><span>₹${Number(bill.gst_amount || 0).toFixed(2)}</span></div>` : ""}
    <div class="row grand"><span>Total</span><span>₹${Number(bill.total || 0).toFixed(2)}</span></div>
  </div>

  ${bill.notes ? `<div class="notes"><strong>Notes:</strong> ${esc(bill.notes)}</div>` : ""}

  <div class="footer">
    Thank you for choosing ${esc(doctor?.clinic_name || doctor?.name || "our clinic")}.<br/>
    Generated on ${new Date().toLocaleString("en-IN")}
  </div>

  <script>
    window.onload = function() {
      setTimeout(function() { window.print(); }, 250);
    };
  </script>
</body>
</html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}

export default BillForm;
